/**
 * SmallUpsConfigurationList — EXISTING/NEW table sections (#996, mirrors #995 RackConfigurationList).
 */

import React, { useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  ActivityIndicator,
  ScrollView,
  TouchableOpacity,
  type StyleProp,
  type ViewStyle,
} from 'react-native';

import { groupSmallUpsByStatus } from './smallUpsListUtils';
import { SmallUpsTableRow, SmallUpsTableHeader } from './SmallUpsTableRow';
import { SMALL_UPS_EMPTY_STATE_TITLE } from '../../constants/smallUpsConfigurationsSubsectionCopy';
import { useResponsive } from '../../hooks/useResponsive';
import { colors, spacing } from '../../theme';
import { DEFAULT_SMALL_UPS_CONFIGURATION } from '../../types/smallUps';
import { FormCompactPrimaryButton } from '../forms/FormCompactPrimaryButton';
import { Plus, AlertCircle, Package } from '../icons';
import {
  resolveRecordTableLayout,
  resolveRecordTableReorderActive,
  useRecordTableSectionReorder,
} from '../recordTable';
import { repeatableSectionShowDragColumn } from '../recordTable/repeatableRowChrome';

import type {
  SmallUpsListSection,
  SmallUpsTableLayout,
} from './smallUpsListUtils';
import type { SmallUpsConfiguration } from '../../types/smallUps';

interface SmallUpsConfigurationListProps {
  units: SmallUpsConfiguration[];
  onSmallUpsPress: (unit: SmallUpsConfiguration) => void;
  onDuplicateSmallUps?: (unit: SmallUpsConfiguration) => void;
  onDeleteSmallUps?: (unit: SmallUpsConfiguration) => void;
  canDeleteSmallUps?: (unit: SmallUpsConfiguration) => boolean;
  onAddSmallUps: () => void;
  /** Web: reorder within EXISTING or NEW section. */
  onReorderSection?: (
    section: SmallUpsListSection,
    fromIndex: number,
    toIndex: number,
  ) => void;
  isLoading?: boolean;
  error?: Error | null;
  /** Blocks all list interaction (e.g. session not ready). */
  disabled?: boolean;
  /** View-only: row opens small UPS details; add/duplicate/delete/reorder hidden. */
  readOnly?: boolean;
  /** Browse-by-location: hide the location column (tag is in the chip filter). */
  hideLocationColumn?: boolean;
  /**
   * Devices view: one flat table (no EXISTING/NEW section headers) with STATUS
   * pills in a column.
   */
  statusInColumn?: boolean;
  allowReorder?: boolean;
  /** Devices: no inner ScrollView — Story 3.9. */
  embeddedInParentScroll?: boolean;
  testID?: string;
  resolveLocationTagName?: (
    locationTagId: number | null | undefined,
  ) => string | null;
}

interface SmallUpsSectionProps {
  title: string;
  section: SmallUpsListSection;
  units: SmallUpsConfiguration[];
  showPlaceholder?: boolean;
  onSmallUpsPress: (unit: SmallUpsConfiguration) => void;
  onDuplicateSmallUps?: (unit: SmallUpsConfiguration) => void;
  onDeleteSmallUps?: (unit: SmallUpsConfiguration) => void;
  canDeleteSmallUps?: (unit: SmallUpsConfiguration) => boolean;
  onAddSmallUps: () => void;
  onReorderSection?: (
    section: SmallUpsListSection,
    fromIndex: number,
    toIndex: number,
  ) => void;
  disabled?: boolean;
  readOnly?: boolean;
  hideLocationColumn?: boolean;
  statusInColumn?: boolean;
  allowReorder?: boolean;
  hideSectionTitle?: boolean;
  /** Flat Devices table only: index where NEW rows begin (= EXISTING count); confines a drag to its status block (#999). */
  reorderStatusBoundary?: number;
  testIDPrefix: string;
  tableLayout: SmallUpsTableLayout;
  resolveLocationTagName?: (
    locationTagId: number | null | undefined,
  ) => string | null;
  showAddButton?: boolean;
}

const PLACEHOLDER_SMALL_UPS: SmallUpsConfiguration = {
  ...DEFAULT_SMALL_UPS_CONFIGURATION,
  id: 0,
  requestId: 0,
  status: 'new',
  displayOrder: 0,
} as SmallUpsConfiguration;

const SmallUpsSection: React.FC<SmallUpsSectionProps> = ({
  title,
  section,
  units,
  showPlaceholder = false,
  onSmallUpsPress,
  onDuplicateSmallUps,
  onDeleteSmallUps,
  canDeleteSmallUps,
  onAddSmallUps,
  onReorderSection,
  disabled,
  readOnly = false,
  allowReorder = false,
  hideLocationColumn = false,
  statusInColumn = false,
  hideSectionTitle = false,
  reorderStatusBoundary,
  testIDPrefix,
  tableLayout,
  resolveLocationTagName,
  showAddButton = true,
}) => {
  const rowCount = units.length + (showPlaceholder ? 1 : 0);
  const showTableChrome = units.length > 0 || showPlaceholder;
  const isCompact = tableLayout === 'compact';
  const reorderActive = resolveRecordTableReorderActive({
    readOnly,
    allowReorder,
    onReorderSection,
    itemCount: units.length,
  });
  const showDragColumn = repeatableSectionShowDragColumn(reorderActive);

  const canReorderTo = useMemo(() => {
    if (reorderStatusBoundary == null) {
      return undefined;
    }
    const boundary = reorderStatusBoundary;
    return (fromIndex: number, toIndex: number) =>
      fromIndex < boundary === toIndex < boundary;
  }, [reorderStatusBoundary]);

  const {
    draggedIndex,
    dragOverIndex,
    registerRowLayout,
    registerRowRemeasure,
    handleDragHandleStart,
    handleDragHandleMove,
    handleDragHandleEnd,
    handleRowDragOver,
    handleRowDrop,
  } = useRecordTableSectionReorder({
    section,
    onReorderSection,
    canReorderTo,
  });

  return (
    <View style={styles.section}>
      {hideSectionTitle ? null : (
        <Text style={styles.sectionTitle}>{title}</Text>
      )}
      {showTableChrome ? (
        <View style={styles.recordTableCard}>
          <SmallUpsTableHeader
            reorderEnabled={reorderActive}
            showDragColumn={showDragColumn}
            tableLayout={tableLayout}
            hideLocationColumn={hideLocationColumn}
            showStatusColumn={statusInColumn}
            readOnly={readOnly}
          />
          {units.map((unit, index) => (
            <SmallUpsTableRow
              key={unit.id ?? `${title}-${unit.displayOrder}`}
              unit={unit}
              onPress={onSmallUpsPress}
              onDuplicate={onDuplicateSmallUps}
              onDelete={
                onDeleteSmallUps && canDeleteSmallUps?.(unit)
                  ? onDeleteSmallUps
                  : undefined
              }
              disabled={disabled}
              readOnly={readOnly}
              isLastInTable={index === rowCount - 1}
              testID={`${testIDPrefix}-row-${unit.id}`}
              tableLayout={tableLayout}
              hideLocationColumn={hideLocationColumn}
              showStatusColumn={statusInColumn}
              reorderEnabled={reorderActive}
              rowIndex={index}
              isDragging={draggedIndex === index}
              isDragOver={dragOverIndex === index}
              onDragHandleStart={handleDragHandleStart}
              onDragHandleMove={handleDragHandleMove}
              onDragHandleEnd={handleDragHandleEnd}
              onRowDragOver={handleRowDragOver}
              onRowDrop={handleRowDrop}
              onRowLayout={registerRowLayout}
              registerRowRemeasure={registerRowRemeasure}
              resolveLocationTagName={resolveLocationTagName}
            />
          ))}
          {showPlaceholder ? (
            <SmallUpsTableRow
              unit={PLACEHOLDER_SMALL_UPS}
              isPlaceholder
              isLastInTable
              tableLayout={tableLayout}
              testID={`${testIDPrefix}-placeholder-row`}
            />
          ) : null}
        </View>
      ) : null}
      {section === 'new' && showAddButton ? (
        <View
          style={[styles.addButtonRow, isCompact && styles.addButtonRowCompact]}
        >
          <AddSmallUpsButton
            onPress={onAddSmallUps}
            disabled={disabled || readOnly}
            testID={`${testIDPrefix}-add-button`}
            variant={isCompact ? 'compact' : 'primary'}
          />
        </View>
      ) : null}
    </View>
  );
};

interface AddSmallUpsButtonProps {
  onPress: () => void;
  disabled?: boolean;
  testID: string;
  variant?: 'primary' | 'compact';
  style?: StyleProp<ViewStyle>;
}

const AddSmallUpsButton: React.FC<AddSmallUpsButtonProps> = ({
  onPress,
  disabled,
  testID,
  variant = 'primary',
  style,
}) => {
  if (variant === 'compact') {
    return (
      <TouchableOpacity
        testID={testID}
        style={[
          styles.addSmallUpsCompactButton,
          disabled && styles.addSmallUpsCompactButtonDisabled,
          style,
        ]}
        onPress={onPress}
        disabled={disabled}
        accessibilityRole="button"
        accessibilityLabel="Add Small UPS"
        activeOpacity={disabled ? 1 : 0.88}
      >
        <View style={styles.addSmallUpsCompactIconWrap}>
          <Plus size={14} color={colors.primary} strokeWidth={2.5} />
        </View>
        <Text style={styles.addSmallUpsCompactLabel}>Add Small UPS</Text>
      </TouchableOpacity>
    );
  }

  return (
    <FormCompactPrimaryButton
      testID={testID}
      onPress={onPress}
      disabled={disabled}
      accessibilityLabel="Add Small UPS"
      icon={<Plus size={14} color={colors.textLight} />}
      style={style}
    >
      Add Small UPS
    </FormCompactPrimaryButton>
  );
};

interface SmallUpsEmptyStateProps {
  onAddSmallUps: () => void;
  disabled?: boolean;
  testID: string;
  addButtonVariant?: 'primary' | 'compact';
}

/** Centered empty state before the first small UPS is added. */
const SmallUpsEmptyState: React.FC<SmallUpsEmptyStateProps> = ({
  onAddSmallUps,
  disabled,
  testID,
  addButtonVariant = 'primary',
}) => (
  <View testID={`${testID}-empty`} style={styles.emptyContainer}>
    <View style={styles.emptyContent}>
      <View style={styles.emptyIconWrap} accessibilityElementsHidden>
        <Package size={48} color={colors.textMuted} />
      </View>
      <Text style={styles.emptyTitle}>{SMALL_UPS_EMPTY_STATE_TITLE}</Text>
      <View style={styles.emptyAddButtonWrap}>
        <AddSmallUpsButton
          onPress={onAddSmallUps}
          disabled={disabled}
          testID={`${testID}-empty-add-button`}
          variant={addButtonVariant}
          style={styles.emptyAddButton}
        />
      </View>
    </View>
  </View>
);

export const SmallUpsConfigurationList: React.FC<
  SmallUpsConfigurationListProps
> = ({
  units,
  onSmallUpsPress,
  onDuplicateSmallUps,
  onDeleteSmallUps,
  canDeleteSmallUps,
  onAddSmallUps,
  onReorderSection,
  isLoading = false,
  error = null,
  disabled = false,
  readOnly = false,
  allowReorder = false,
  hideLocationColumn = false,
  statusInColumn = false,
  embeddedInParentScroll = false,
  testID = 'small-ups-configuration-list',
  resolveLocationTagName,
}) => {
  const { dimensions } = useResponsive();
  const tableLayout: SmallUpsTableLayout = resolveRecordTableLayout(
    dimensions.width,
  );
  const isCompactLayout = tableLayout === 'compact';

  const grouped = useMemo(() => groupSmallUpsByStatus(units), [units]);
  const hasExisting = grouped.existing.length > 0;
  const hasNew = grouped.new.length > 0;
  const showNewPlaceholder =
    !hasNew && !hasExisting && (Platform.OS !== 'web' || isCompactLayout);
  const showNewSection = hasNew || showNewPlaceholder || !readOnly;

  if (isLoading) {
    return (
      <View testID={`${testID}-loading`} style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
        <Text style={styles.loadingText}>Loading small UPS...</Text>
      </View>
    );
  }

  if (error) {
    return (
      <View testID={`${testID}-error`} style={styles.errorContainer}>
        <AlertCircle size={24} color={colors.error} />
        <Text style={styles.errorText}>Failed to load small UPS</Text>
        <Text style={styles.errorSubtext}>
          Please try again or contact support if the problem continues.
        </Text>
      </View>
    );
  }

  if (units.length === 0) {
    const emptyShell = (
      <SmallUpsEmptyState
        onAddSmallUps={onAddSmallUps}
        disabled={disabled || readOnly}
        testID={testID}
        addButtonVariant={isCompactLayout ? 'compact' : 'primary'}
      />
    );

    if (isCompactLayout && !embeddedInParentScroll) {
      return (
        <ScrollView
          testID={testID}
          style={styles.container}
          contentContainerStyle={styles.emptyScrollContent}
          keyboardShouldPersistTaps="handled"
        >
          {emptyShell}
        </ScrollView>
      );
    }

    return (
      <View testID={testID} style={styles.container}>
        {emptyShell}
      </View>
    );
  }

  const renderFlatList = () => {
    if (statusInColumn) {
      const flatUnits = [...grouped.existing, ...grouped.new];
      return (
        <SmallUpsSection
          title=""
          section="existing"
          units={flatUnits}
          onSmallUpsPress={onSmallUpsPress}
          onDuplicateSmallUps={onDuplicateSmallUps}
          onDeleteSmallUps={onDeleteSmallUps}
          canDeleteSmallUps={canDeleteSmallUps}
          onAddSmallUps={onAddSmallUps}
          onReorderSection={onReorderSection}
          disabled={disabled}
          readOnly={readOnly}
          allowReorder={allowReorder}
          hideLocationColumn={hideLocationColumn}
          statusInColumn
          hideSectionTitle
          reorderStatusBoundary={grouped.existing.length}
          showAddButton={false}
          testIDPrefix={`${testID}-all`}
          tableLayout={tableLayout}
          resolveLocationTagName={resolveLocationTagName}
        />
      );
    }

    return (
      <>
        {hasExisting ? (
          <SmallUpsSection
            title="EXISTING"
            section="existing"
            units={grouped.existing}
            onSmallUpsPress={onSmallUpsPress}
            onDuplicateSmallUps={onDuplicateSmallUps}
            onDeleteSmallUps={onDeleteSmallUps}
            canDeleteSmallUps={canDeleteSmallUps}
            onAddSmallUps={onAddSmallUps}
            onReorderSection={onReorderSection}
            disabled={disabled}
            readOnly={readOnly}
            allowReorder={allowReorder}
            hideLocationColumn={hideLocationColumn}
            showAddButton={!readOnly}
            testIDPrefix={`${testID}-existing`}
            tableLayout={tableLayout}
            resolveLocationTagName={resolveLocationTagName}
          />
        ) : null}

        {showNewSection ? (
          <SmallUpsSection
            title="NEW"
            section="new"
            units={grouped.new}
            hideSectionTitle={!hasNew}
            showPlaceholder={showNewPlaceholder}
            onSmallUpsPress={onSmallUpsPress}
            onDuplicateSmallUps={onDuplicateSmallUps}
            onDeleteSmallUps={onDeleteSmallUps}
            canDeleteSmallUps={canDeleteSmallUps}
            onAddSmallUps={onAddSmallUps}
            onReorderSection={onReorderSection}
            disabled={disabled}
            readOnly={readOnly}
            allowReorder={allowReorder}
            hideLocationColumn={hideLocationColumn}
            showAddButton={!readOnly}
            testIDPrefix={`${testID}-new`}
            tableLayout={tableLayout}
            resolveLocationTagName={resolveLocationTagName}
          />
        ) : null}
      </>
    );
  };

  const listBody = renderFlatList();
  if (embeddedInParentScroll) {
    return (
      <View testID={testID} style={styles.container}>
        {listBody}
      </View>
    );
  }

  return (
    <ScrollView
      testID={testID}
      style={styles.container}
      contentContainerStyle={styles.contentContainer}
      keyboardShouldPersistTaps="handled"
    >
      {listBody}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    paddingBottom: spacing.lg,
  },
  emptyScrollContent: {
    flexGrow: 1,
    paddingBottom: spacing.lg,
  },
  section: {
    marginBottom: spacing.lg,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: '700',
    color: colors.textMuted,
    letterSpacing: 0.7,
    marginBottom: spacing.sm,
    textTransform: 'uppercase',
  },
  recordTableCard: {
    borderWidth: 1,
    borderColor: colors.borderCard,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: colors.backgroundPrimary,
    ...Platform.select({
      default: {},
      web: {
        minWidth: 0,
      },
    }),
  },
  loadingContainer: {
    padding: spacing.xl,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    fontSize: 14,
    color: colors.textSecondary,
    marginTop: spacing.sm,
  },
  errorContainer: {
    padding: spacing.xl,
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorText: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.error,
    marginTop: spacing.sm,
  },
  errorSubtext: {
    fontSize: 14,
    color: colors.textSecondary,
    marginTop: spacing.xs,
    textAlign: 'center',
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.lg,
    minHeight: Platform.OS === 'web' ? 480 : 320,
  },
  emptyContent: {
    alignItems: 'center',
    gap: spacing.sm,
    width: '100%',
    maxWidth: 360,
  },
  emptyIconWrap: {
    marginBottom: spacing.xs,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.textPrimary,
    textAlign: 'center',
    marginTop: spacing.xs,
  },
  emptyAddButtonWrap: {
    marginTop: spacing.md,
    alignItems: 'center',
    alignSelf: 'stretch',
  },
  emptyAddButton: {
    alignSelf: 'center',
  },
  addButtonRow: {
    width: '100%',
    marginTop: spacing.md,
  },
  addButtonRowCompact: {
    marginTop: spacing.sm,
  },
  addSmallUpsCompactButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    width: '100%',
    minHeight: 48,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.md,
    borderRadius: 8,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: colors.textMuted,
    backgroundColor: colors.backgroundPrimary,
  },
  addSmallUpsCompactButtonDisabled: {
    opacity: 0.5,
  },
  addSmallUpsCompactIconWrap: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.backgroundPrimary,
  },
  addSmallUpsCompactLabel: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.primary,
  },
});
